import '../App.css';
import '../styles/loading-states.css';
import { CustomInterval } from '../partials/CustomInterval';
import { DimTablesPeek } from '../partials/DimTablesPeek';
import { ViewLastRunHistory } from '../partials/ViewLastRunHistory';
import { ReprocessRollUp } from '../partials/ReprocessRollUp';
import { ReprocessSingleEvent } from '../partials/ReprocessSingleEvent';
import { useEffect, useState } from 'react';
import { getBIParams } from '../services/dimension-service';
import { CurrentYear } from '../partials/CurrentYear';
import { PullingYears } from '../partials/PullingYears';
import { useGetToken } from '../hooks/custom-hooks';
import { LoadingModule } from '../partials/LoadingModule';
import { LoadingHistory } from '../partials/LoadingHistory';
import { FaAngleDoubleRight, FaBolt, FaDatabase, FaHistory } from 'react-icons/fa';
import { AreYouSureModal } from '../pop-ups/are-you-sure';

export const HomePage = () => {

    const token = useGetToken();

    const [currentYear, setCurrentYear] = useState();
    const [stagingNextSeason, setStagingNextSeason] = useState();
    const [isLoading, setIsLoading] = useState(true);
    const [yearDropdownList, setYearDropdownList] = useState([]);
    const [showRolloverConfirm, setShowRolloverConfirm] = useState(false);

    const handleRolloverConfirm = () => {
        console.log(`Year rollover confirmed: ${currentYear} → ${Number(currentYear) + 1}`);
        setShowRolloverConfirm(false);
    };

    useEffect(() => {

        if (token !== null) {
            const getBIVariables = async () => {

                let currentParams = await getBIParams(token);
                const params = currentParams.data;

                if (!params) {
                    console.error("Unable to load BI parameters:", currentParams.error);
                    setIsLoading(false);
                    return;
                }

                setCurrentYear(params.seasonYear);
                setStagingNextSeason(params.stageNextSeason);
                setYearDropdownList(params.yearDropDownList ?? []);
                setIsLoading(false);
            }
            getBIVariables(); 
        }
    }, [token]);

    if(isLoading) {
        return(
            <main className='App home-page' aria-busy='true'>
                <div className='home-page__masthead'>
                    <div className='home-page__intro'>
                        <span className='home-page__eyebrow'>Ticket administration</span>
                        <h1>Operations dashboard</h1>
                        <p>Loading the latest data and run activity.</p>
                    </div>
                    <div className='home-page__live-status'>
                        <span className='home-page__pulse'></span>
                        Syncing workspace
                    </div>
                </div>
                <div className='app-row home-page__grid'>
                    <section className='half-screen-column home-page__panel home-page__panel--primary'>
                        <div className='home-page__section-heading'>
                            <span className='home-page__section-icon'><FaDatabase /></span>
                            <div>
                                <span>Data quality</span>
                                <small>Dimension tables</small>
                            </div>
                        </div>
                        <LoadingModule/>
                    </section>
                    <section className='half-screen-column home-page__panel'>
                        <div className='module-container-biParams home-page__year-summary home-page__year-summary--loading'>
                            <div className='home-page__year-skeleton'>
                                <span className='loading-line'></span>
                                <span className='loading-line'></span>
                            </div>
                            <div className='home-page__year-skeleton'>
                                <span className='loading-line'></span>
                                <span className='loading-line'></span>
                            </div>
                        </div>
                        <div className='home-page__section-heading'>
                            <span className='home-page__section-icon'><FaHistory /></span>
                            <div>
                                <span>Retrieval activity</span>
                                <small>Recent process history</small>
                            </div>
                        </div>
                        <LoadingHistory/>
                    </section>
                    <section className='half-screen-column home-page__panel home-page__panel--actions'>
                        <div className='home-page__section-heading'>
                            <span className='home-page__section-icon'><FaBolt /></span>
                            <div>
                                <span>Quick actions</span>
                                <small>Season and retrieval controls</small>
                            </div>
                        </div>
                        <div className='home-page__action-stack home-page__action-stack--loading'>
                            <div className='loading-line'></div>
                            <div className='loading-line'></div>
                            <div className='loading-line'></div>
                            <div className='loading-line'></div>
                        </div>
                    </section>
                </div>
            </main>
        );
    }

    return (
        <main className='App home-page'>
            <div className='home-page__masthead'>
                <div className='home-page__intro'>
                    <span className='home-page__eyebrow'>Ticket administration</span>
                    <h1>Operations dashboard</h1>
                    <p>Monitor dimension tables, retrieval activity, and maintenance tasks in one place.</p>
                </div>
                <div className='home-page__live-status'>
                    <span className='home-page__pulse'></span>
                    Live workspace
                </div>
            </div>
            <div className='app-row home-page__grid'>
                <section className='half-screen-column home-page__panel home-page__panel--primary'>
                    <div className='home-page__section-heading'>
                        <span className='home-page__section-icon'><FaDatabase /></span>
                        <div>
                            <span>Data quality</span>
                            <small>Dimension tables</small>
                        </div>
                    </div>
                    <DimTablesPeek seasonYear={currentYear} yearDropdownList={yearDropdownList}/>
                </section>
                <section className='half-screen-column home-page__panel'>
                    <div className='module-container-biParams home-page__year-summary'>
                        <CurrentYear currentYear={currentYear}/>
                        <PullingYears currentYear={currentYear} currentStatus={stagingNextSeason}/>                    
                    </div>
                    <div className='home-page__section-heading'>
                        <span className='home-page__section-icon'><FaHistory /></span>
                        <div>
                            <span>Retrieval activity</span>
                            <small>Recent process history</small>
                        </div>
                    </div>
                    <ViewLastRunHistory/> 
                </section>
                <section className='half-screen-column home-page__panel home-page__panel--actions'>
                    <div className='home-page__section-heading'>
                        <span className='home-page__section-icon'><FaBolt /></span>
                        <div>
                            <span>Quick actions</span>
                            <small>Season and retrieval controls</small>
                        </div>
                    </div>
                    <div className='home-page__action-stack'>
                        <CustomInterval/>
                        <ReprocessRollUp
                            currentYear={currentYear}
                            yearDropdownList={yearDropdownList}
                            rollupType={"ticketRollup"}
                        />
                        <ReprocessSingleEvent/>
                        <button
                            type='button'
                            className='module-container-slim home-page__rollover-action'
                            onClick={() => setShowRolloverConfirm(true)}>
                            <span className='icon-text-row App-text'>
                                <FaAngleDoubleRight className='teal-icons'/>
                                Advance to {Number(currentYear) + 1}
                            </span>
                        </button>
                    </div>
                </section>
            </div>
            <AreYouSureModal
                isVisible={showRolloverConfirm}
                loadingState={false}
                bodyText={`Advance the BI year from ${currentYear} to ${Number(currentYear) + 1}?`}
                handleCancelAction={() => setShowRolloverConfirm(false)}
                onCompleteAction={handleRolloverConfirm}
            />
        </main>
    );
}
